import React, { Suspense, lazy, useState } from 'react';
import { Routes, Route } from 'react-router-dom';

import Navbar from './Component/Navbar.jsx';
import Newabout from './Component/AboutUs/Newabout.jsx';
import Ourcap from './Component/Services/Ourcap.jsx';
import Success from './Component/Testimonial/Success.jsx';
import WhyVzcorn from './Component/ConsultancyPlan/WhyVizcorn.jsx';
import Footer from './Component/Footer/VizcornFooter.jsx';

import useSEO from './hooks/useSEO.js';
import { useTranslation } from 'react-i18next';
import { RootRedirect, LangGate } from './i18n/LangRouting.jsx';
import LazyMount from './Component/LazyMount.jsx';

// Route-level code splitting: Careers/Terms/Privacy/Cookies are never
// needed for someone landing on "/", so they shouldn't ship in the same
// JS chunk as the homepage. This alone was a large share of the 1.19 MB
// single-bundle payload flagged by Lighthouse (reduce-unused-javascript /
// total-blocking-time).
const Terms = lazy(() => import('./Component/LegalLinks/Terms.jsx'));
const Privacy = lazy(() => import('./Component/LegalLinks/Privacy.jsx'));
const Cookies = lazy(() => import('./Component/LegalLinks/Cookies.jsx'));
const VizcornCareersHero = lazy(() => import('./Component/CareersPage/Vizcorncareers.jsx'));

// GlobalOperations pulls in three.js + d3 for a below-the-fold 3D globe —
// by far the heaviest dependency in the app. Splitting it into its own
// chunk keeps three.js/d3 out of the code needed for first paint; wrapping
// it in <LazyMount> (see that file) additionally defers even requesting
// that chunk until the section scrolls near the viewport, so it no longer
// competes with the hero/nav/CTA for main-thread time during load.
const GlobalOperationsImpactGlobe = lazy(() => import('./Component/GlobalOperationsMap/GlobalOperations.jsx'));

const Home = () => {
  // Tracked purely so Navbar's scroll-spy (onActiveTabChange) has a
  // place to report the currently visible section — no UI consumes it.
  const [activeTab, setActiveTab] = useState('all');
  const { t } = useTranslation();

  useSEO(t('seo.homeTitle'), t('seo.homeDescription'));

  return (
    <>
      <Navbar onActiveTabChange={setActiveTab} />

      {/* <main> landmark: Lighthouse/axe flagged the document as having no
          main landmark, which also matters for real screen-reader users
          skipping past the nav. Nav + Footer stay outside it, as usual. */}
      <main>
        {/* We Are — Hero / About */}
        <div id="AboutSection" className="mt-6">
          <Newabout />
        </div>

        {/* We Deliver — Capabilities */}
        <div id="DesignServices">
          <Ourcap />
        </div>

        {/* Why Vizcorn — Success stories */}
        <div id="WhySection">
          <Success />
        </div>

        {/* Build Together / Contact — Consultation booking */}
        <div id="ContactUs">
          <WhyVzcorn />
        </div>

        {/* Global operations map — three.js + d3, code-split and deferred
            until it's about to scroll into view (see LazyMount.jsx) */}
        <LazyMount minHeight={640}>
          <Suspense fallback={<div style={{ minHeight: 640 }} aria-hidden="true" />}>
            <GlobalOperationsImpactGlobe />
          </Suspense>
        </LazyMount>
      </main>

      <Footer />
    </>
  );
};

const CareersPage = () => {
  const { t } = useTranslation();
  useSEO(t('seo.careersTitle'), t('seo.careersDescription'));

  return <VizcornCareersHero />;
};

// Simple full-page placeholder shown for the instant it takes to fetch a
// lazy route chunk (Careers/Terms/Privacy/Cookies) — these are separate
// network requests now instead of being baked into the homepage bundle.
const RouteFallback = () => (
  <div style={{ minHeight: '100vh' }} aria-hidden="true" />
);

const App = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* "/" picks a language (saved choice, else browser language) and
            redirects to "/en" or "/de" — see src/i18n/LangRouting.jsx */}
        <Route path="/" element={<RootRedirect />} />

        <Route path="/:lang" element={<LangGate><Home /></LangGate>} />
        <Route path="/:lang/careers" element={<LangGate><CareersPage /></LangGate>} />
        <Route path="/:lang/terms" element={<LangGate><Terms /></LangGate>} />
        <Route path="/:lang/privacy" element={<LangGate><Privacy /></LangGate>} />
        <Route path="/:lang/cookies" element={<LangGate><Cookies /></LangGate>} />

        {/* Unknown paths (including old un-prefixed links like /terms) fall
            back through "/" so they still land on a valid language route */}
        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </Suspense>
  );
};

export default App;
